import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Application, PurchaseProductRequest } from '@raydiant/api-client-js';
import apiClient from '../../clients/miraClient';
import {
  selectUserProfile,
  selectHasAccessToInvoices,
  selectIsEnterpriseUser,
} from '../../selectors/user';
import useMarketPlaceApplications from '../../hooks/useMarketPlaceApplications';
import useOutstandingBalance from '../../hooks/useOutstandingBalance';
import Page from '../../components/Page';
import MarketplaceAppDialog from '../../components/MarketplaceAppDialog';
import * as applicationsPageActions from '../ApplicationsPage/actions';
import {
  sendFreetrialRequest,
  getApplicationName,
  userHasPremiumApp,
} from './MarketplacePageUtils';
import { MarketPlaceInquiry } from './MarketplacePage.interface';
import FreetrialModal from './components/FreetrialModal';
import OutstandingInvoicesDialog from './components/OutstandingInvoicesDialog';
import ApplicationsLayout from './components/ApplicationsLayout';
import PurchaseProductModal from './components/PurchaseProductModal';
import ManualSetUpModal from './components/ManualSetUpModal';
import PurchaseSuccessModal from './components/PurchaseSuccessModal';
import { LoadingStatus } from '../../components/LoadingButton/LoadingButton';
import { useSnackbar } from 'notistack';
import CustomPricingModal from './components/CustomPricingModal';
import ReachOutToSalesModal from './components/ReachOutToSalesModal';
import config from '../../config';
import MarketplaceBanner from '../../assets/noisy-gradients.webp';
import DisplaiLogo from '../../assets/displai-logo.svg';
import PageLayout from '../../components/PageLayout/PageLayout';
import Card from '@raydiant/raydial/components/Card';
import TextField from '@raydiant/raydial/components/TextField';
import Button from '@raydiant/raydial/components/Button';
import cn from 'classnames';
import { selectApplications } from '../../selectors/applications';
import _ from 'lodash';

const { accountManagementUrl } = config;

export interface MarketplaceApplication extends Application {
  checked?: boolean;
}

export interface FilterItem {
  key: string;
  value: string;
}

const MARKETPLACE_FILTERS: FilterItem[] = [
  {
    key: 'all',
    value: 'All',
  },
  {
    key: 'content',
    value: 'Content',
  },
  {
    key: 'menu',
    value: 'Menu',
  },
  {
    key: 'communication',
    value: 'Communication',
  },
];

const MarketplacePage: FC = () => {
  const [selectedModal, setSelectedModal] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] =
    useState<Application | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitStatus, setSubmitStatus] = useState<LoadingStatus>('idle');
  const [search, setSearch] = useState('');
  const [isMyApps, setIsMyApps] = useState(false);
  const [filter, setFilter] = useState('all');

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useSelector(selectUserProfile);
  const hasAccessToInvoices = useSelector(selectHasAccessToInvoices);
  const isEnterpriseUser = useSelector(selectIsEnterpriseUser);
  const usersApplications = useSelector(selectApplications);

  const { data: applicationsData, isLoading: isApplicationsLoading } =
    useMarketPlaceApplications();
  const {
    data: outstandingBalanceData,
    isLoading: isOutstandingBalanceDataLoading,
  } = useOutstandingBalance();

  const isLoading = isApplicationsLoading || isOutstandingBalanceDataLoading;
  const isShowReachOutToSalesModal = !!selectedModal?.split('-')[1];
  const tileModalName = selectedModal?.split('-')[0] || '';

  const filteredApplications = useMemo(() => {
    const filtered = applicationsData?.filter((app) => {
      const matchesFilter =
        filter === 'all' ||
        app.applicationCategory?.name?.toLowerCase() === filter;
      const matchesSearch = app.name
        .toLowerCase()
        .includes(search.toLowerCase());
      const matchesMyApps = isMyApps
        ? userHasPremiumApp(app, usersApplications || [])
        : true;
      return matchesFilter && matchesSearch && matchesMyApps;
    });

    return _.sortBy(filtered, (app) =>
      (app.currentAppVersion?.name || app.name)?.toLowerCase(),
    );
  }, [applicationsData, filter, search, isMyApps, usersApplications]);

  const loadApplications = useCallback(() => {
    dispatch(applicationsPageActions.loadApplicationsPage());
  }, [dispatch]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  useEffect(() => {
    const handleSegmentMarketplaceLoad = () => {
      analytics.track('Marketplace Page Loaded', {
        user_id: currentUser?.id,
        user_name: currentUser?.name,
      });
    };

    handleSegmentMarketplaceLoad();
  }, [currentUser]);

  if (!currentUser) return null;

  const isDisplaiEmployee =
    currentUser.email.includes('@displai.ai') ||
    currentUser.email.includes('@raydiant.com');
  const isOutstandingBalanceUser =
    outstandingBalanceData && outstandingBalanceData.hasOutstandingInvoices;
  const isFreeTrialUser = currentUser.isFreetrialUser && !isDisplaiEmployee;
  const determineUserStatus = () => {
    if (isOutstandingBalanceUser) {
      return 'outstandingInvoices';
    } else if (isFreeTrialUser) {
      return 'freeTrial';
    } else if (isEnterpriseUser) {
      return 'enterpriseUser';
    } else {
      return 'regular';
    }
  };
  const userStatus = determineUserStatus();
  const cta =
    selectedApplication?.currentAppVersion?.name || selectedApplication?.name;

  const sendMarketPlaceInquiry = async (params: MarketPlaceInquiry) => {
    try {
      await apiClient.sendMarketPlaceInquiry(params);
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    }
  };

  const handleSegmentMarketplaceAppGetPressed = (selected: string) =>
    analytics.track('Marketplace App Get Button Pressed', {
      user_id: currentUser.id,
      user_name: currentUser.name,
      app_name: selected,
    });

  const handleSegmentMarketplaceGetConfirmed = (selected: string) =>
    analytics.track('Marketplace Get Confirmed ', {
      user_id: currentUser.id,
      app_name: selected,
    });

  const handleSegmentMarketplaceAppPurchased = (selected: string) =>
    analytics.track('Marketplace App Purchased ', {
      user_id: currentUser.id,
      app_name: selected,
    });

  const handleDialogClick = (selected: string) => {
    handleSegmentMarketplaceGetConfirmed(selected);
    sendMarketPlaceInquiry({ app: selected });
  };

  const handleFreetrialDialogClick = () => {
    if (selectedApplication) {
      handleSegmentMarketplaceAppGetPressed(
        getApplicationName(selectedApplication),
      );
    }
    if (sendFreetrialRequest()) {
      sendMarketPlaceInquiry({ app: 'Permission Update' });
    }
  };

  const handleAppCardBuyButtonClick = (application: Application) => {
    handleSegmentMarketplaceAppGetPressed(getApplicationName(application));
    setSelectedApplication(application);
    setSelectedModal('purchaseProduct');
  };

  const handleClose = () => {
    setError(null);
    setSelectedModal(null);
    setSelectedApplication(null);
  };

  const handleOutstandingInvoicesClick = () => {
    if (hasAccessToInvoices) {
      window.open(`${accountManagementUrl}/invoices`);
    }
    handleClose();
  };

  const handleCustomPricing = () => {
    sendMarketPlaceInquiry({ app: `Tailor Pricing Needed. app: ${cta}` });
    handleClose();
  };

  const openCustomPricingModal = () => {
    setSelectedModal('customPricing');
  };

  const handleReachOutToSalesClick = () => {
    handleFreetrialDialogClick();
    handleClose();
  };

  const handlePurchaseProductModalClick = async (
    orderDetail: PurchaseProductRequest,
    manualEnablementRequired: boolean,
  ) => {
    try {
      setSubmitStatus('loading');

      const { response } = await apiClient.purchaseProduct(orderDetail);
      setSubmitStatus('success');

      if (manualEnablementRequired) {
        setSelectedModal('manualSetUp');
        sendMarketPlaceInquiry({
          app: `Manual Setup Needed. quoteId: ${response.quoteId}`,
        });
      } else {
        setSelectedModal('purchaseSuccess');
      }

      selectedApplication &&
        handleSegmentMarketplaceAppPurchased(
          selectedApplication.currentAppVersion.name ||
            selectedApplication.name,
        );
      enqueueSnackbar('Successfully purchased an application', {
        variant: 'success',
      });
    } catch (error) {
      console.log('ERROR PURCHASING MARKETPLACE APPLICATION', error);

      enqueueSnackbar('There was an error purchasing an application', {
        variant: 'error',
      });

      setSubmitStatus('idle');
    }
  };

  return (
    <Page>
      <PageLayout title="">
        <Card className="p-0">
          <div
            className="w-full h-auto min-h-[200px] rounded-t-3xl mb-7 flex flex-col justify-center items-center"
            style={{
              backgroundImage: `url(${MarketplaceBanner})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <img
              src={DisplaiLogo}
              alt="Displai"
              className="h-[90px] w-auto mt-1"
            />
            <span className="text-[20px] text-white">App Marketplace</span>
          </div>

          <div className="px-4 md:px-7">
            <div className="flex flex-col justify-between items-center gap-4 md:flex-row">
              <TextField
                placeholder="Search apps"
                value={search}
                onChange={(value) => setSearch(value)}
                className="w-full md:max-w-[350px]"
              />

              <div className="w-full grid grid-cols-2 md:w-auto">
                <Button
                  variant="unstyled"
                  className={cn(
                    'border border-2 border-gray-400 border-r-0 font-semibold px-6 py-3 rounded-l-xl',
                    isMyApps && 'bg-primary border-primary text-white',
                  )}
                  onPress={() => setIsMyApps(true)}
                >
                  My Apps
                </Button>
                <Button
                  variant="unstyled"
                  className={cn(
                    'border border-2 border-gray-400 border-l-0 font-semibold px-6 py-3 rounded-r-xl',
                    !isMyApps && 'bg-primary border-primary text-white',
                  )}
                  onPress={() => setIsMyApps(false)}
                >
                  All Available
                </Button>
              </div>
            </div>
            <div className="flex gap-3 overflow-x-auto pt-5 py-4">
              {MARKETPLACE_FILTERS.map((f) => (
                <Button
                  key={f.key}
                  variant="unstyled"
                  className={cn(
                    'bg-gray-100 border border-gray-200 text-black px-5 py-1.5 rounded-2xl font-semibold',
                    filter === f.key && 'bg-primary text-white border-primary',
                  )}
                  onPress={() => setFilter(f.key)}
                >
                  {f.value}
                </Button>
              ))}
            </div>
          </div>

          <div className="px-4 py-8 md:px-7">
            <ApplicationsLayout
              applications={filteredApplications}
              isLoading={isLoading}
              onClick={handleAppCardBuyButtonClick}
              selectedFilterGroup={filter}
              onDefaultMarketplaceCardClick={() => setIsMyApps(false)}
            />

            {selectedApplication && (
              <>
                {userStatus === 'freeTrial' && (
                  <FreetrialModal
                    open={userStatus === 'freeTrial'}
                    onClick={handleFreetrialDialogClick}
                    onClose={handleClose}
                  />
                )}

                {userStatus === 'enterpriseUser' && (
                  <ReachOutToSalesModal
                    open={userStatus === 'enterpriseUser'}
                    onClick={handleReachOutToSalesClick}
                  />
                )}

                {userStatus === 'outstandingInvoices' && (
                  <OutstandingInvoicesDialog
                    open={userStatus === 'outstandingInvoices'}
                    hasPermission={hasAccessToInvoices}
                    onClick={handleOutstandingInvoicesClick}
                    onCancel={handleClose}
                  />
                )}

                {userStatus === 'regular' && (
                  <>
                    <PurchaseProductModal
                      open={selectedModal === 'purchaseProduct'}
                      selectedApplication={selectedApplication}
                      submitStatus={submitStatus}
                      onClick={handlePurchaseProductModalClick}
                      onCustomPricing={openCustomPricingModal}
                      onClose={handleClose}
                    />
                    <ManualSetUpModal
                      open={selectedModal === 'manualSetUp'}
                      onClick={handleClose}
                    />
                    <PurchaseSuccessModal
                      open={selectedModal === 'purchaseSuccess'}
                      onClick={handleClose}
                    />
                    {cta && (
                      <CustomPricingModal
                        open={selectedModal === 'customPricing'}
                        appName={cta}
                        onClick={handleCustomPricing}
                        onClose={handleClose}
                      />
                    )}
                  </>
                )}
              </>
            )}

            <MarketplaceAppDialog
              open={isShowReachOutToSalesModal}
              handleClick={handleDialogClick}
              handleClose={handleClose}
              name={tileModalName}
              error={error}
            />
          </div>
        </Card>
      </PageLayout>
    </Page>
  );
};

export default MarketplacePage;
